// Settings -> Proposal Content tab: the four tenant catalogues a proposal's SELECTED_CONTENT
// sections choose from — payment terms, terms & conditions clauses, exclusions, warranty terms.
//
// Why this screen exists: PAYMENT_TERMS / TERMS_CONDITIONS / EXCLUSIONS / WARRANTY are built by
// SELECTING from these lists, never by generating text. Every tenant is now provisioned with a
// default set at registration (api-service app/domain/proposals/tenant_defaults.py), so a
// prepared proposal is never blank — but the defaults are generic boilerplate, and this is where
// a Tenant Admin makes them theirs.
//
// Deleting is a soft delete server-side: a proposal built from an entry keeps the copy it
// snapshotted, so removing one here only takes it out of future proposals and out of the pickers.
// Deactivated entries stay listed (greyed, with a Restore button) rather than vanishing.
//
// Separate file rather than another section inside TenantSettingsPage.tsx (already ~3,200 lines),
// matching InventorySection.tsx — it owns real per-row editing state for four lists.
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useCreateExclusionMutation, useCreatePaymentTermMutation, useCreateTermClauseMutation,
  useCreateWarrantyTermMutation, useDeleteExclusionMutation, useDeletePaymentTermMutation,
  useDeleteTermClauseMutation, useDeleteWarrantyTermMutation, useListExclusionsQuery,
  useListPaymentTermsQuery, useListTermClausesQuery, useListWarrantyTermsQuery,
  useUpdateExclusionMutation, useUpdatePaymentTermMutation, useUpdateTermClauseMutation,
  useUpdateWarrantyTermMutation,
  type ExclusionItem, type PaymentScheduleMilestone, type PaymentTerm, type TermClause,
  type WarrantyTerm,
} from '@/redux/internal-services/proposalsApi';
import { apiErrorMessage } from '@/utils/apiError';
import { cn } from '@/utils/cn';
import { Alert } from '@/components/UI/Alert';
import { Button } from '@/components/UI/Button';
import { IconButton } from '@/components/UI/IconButton';
import { LoadingState } from '@/components/UI/LoadingState';
import { Switch } from '@/components/UI/Switch';
import { Tabs } from '@/components/UI/Tabs';
import { fieldClass } from '@/components/UI/fieldStyles';

type CatalogueTab = 'payment_terms' | 'terms' | 'exclusions' | 'warranty';

// ── Shared row chrome ─────────────────────────────────────────────────────────

/** One catalogue row. Inactive rows stay visible at half opacity with Restore in place of
 *  Remove — a tenant who switches something off needs to be able to find it again. */
const Row: React.FC<{
  active: boolean;
  busy: boolean;
  onRemove: () => void;
  onRestore: () => void;
  children: React.ReactNode;
}> = ({ active, busy, onRemove, onRestore, children }) => {
  const { t } = useTranslation();
  return (
    <div className={cn('rounded-control border border-ui-border bg-ui-subtle p-3', !active && 'opacity-55')}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">{children}</div>
        <Button
          type="button"
          variant="outline"
          size="xs"
          disabled={busy}
          onClick={active ? onRemove : onRestore}
          className="shrink-0"
        >
          {active
            ? t('settings.proposalContentRemove', 'Remove')
            : t('settings.proposalContentRestore', 'Restore')}
        </Button>
      </div>
    </div>
  );
};

/** Commits on blur, matching the estimate and proposal editors — there is no Save button on any
 *  row in this screen. Escape abandons the edit. */
const InlineField: React.FC<{
  value: string;
  multiline?: boolean;
  placeholder?: string;
  disabled?: boolean;
  onCommit: (next: string) => void;
  className?: string;
}> = ({ value, multiline, placeholder, disabled, onCommit, className }) => {
  const [draft, setDraft] = useState(value);
  const [dirty, setDirty] = useState(false);

  // While untouched the field mirrors the server; once edited it is the user's draft until blur.
  const shown = dirty ? draft : value;
  const commit = () => {
    setDirty(false);
    if (draft !== value && dirty) onCommit(draft);
  };
  const common = {
    value: shown,
    placeholder,
    disabled,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setDirty(true);
      setDraft(e.target.value);
    },
    onBlur: commit,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') { setDirty(false); setDraft(value); (e.target as HTMLElement).blur(); }
    },
  };
  return multiline
    ? <textarea {...common} rows={Math.max(2, Math.ceil(shown.length / 95))} className={cn(fieldClass({ shape: 'box', size: 'xs' }), 'resize-y leading-relaxed', className)} />
    : <input {...common} className={cn(fieldClass({ size: 'xs' }), className)} />;
};

const AddRow: React.FC<{ label: string; onAdd: () => void; busy: boolean }> = ({ label, onAdd, busy }) => (
  <Button type="button" variant="soft" size="sm" onClick={onAdd} disabled={busy}>
    + {label}
  </Button>
);

// ── Payment terms ─────────────────────────────────────────────────────────────

const PaymentTermsPanel: React.FC<{ onError: (msg: string) => void }> = ({ onError }) => {
  const { t } = useTranslation();
  const { data: terms = [], isLoading } = useListPaymentTermsQuery({ includeInactive: true });
  const [create, { isLoading: creating }] = useCreatePaymentTermMutation();
  const [update, { isLoading: updating }] = useUpdatePaymentTermMutation();
  const [remove, { isLoading: removing }] = useDeletePaymentTermMutation();
  const busy = creating || updating || removing;

  const run = async (fn: () => Promise<unknown>, fallback: string) => {
    try { await fn(); } catch (e) { onError(apiErrorMessage(e, t, fallback)); }
  };

  const patchSchedule = (term: PaymentTerm, idx: number, patch: Partial<PaymentScheduleMilestone>) => {
    const payment_schedule = term.payment_schedule.map((m, i) => (i === idx ? { ...m, ...patch } : m));
    void run(
      () => update({ payment_term_code: term.payment_term_code, payment_schedule }).unwrap(),
      t('settings.proposalContentSaveFailed', 'Could not save that change.'),
    );
  };

  if (isLoading) return <LoadingState label={t('common.loading', 'Loading…')} className="py-6" />;

  return (
    <div className="space-y-3">
      <p className="text-xs leading-relaxed text-ui-text3">
        {t(
          'settings.proposalContentPaymentTermsHint',
          'The payment schedules a proposal can offer. The one marked Default is what a newly prepared proposal starts with.',
        )}
      </p>

      {terms.map((term) => (
        <Row
          key={term.payment_term_code}
          active={term.active}
          busy={busy}
          onRemove={() => void run(() => remove(term.payment_term_code).unwrap(), t('settings.proposalContentRemoveFailed', 'Could not remove that entry.'))}
          onRestore={() => void run(() => update({ payment_term_code: term.payment_term_code, active: true }).unwrap(), t('settings.proposalContentSaveFailed', 'Could not save that change.'))}
        >
          <div className="flex items-center gap-2">
            <InlineField
              value={term.name}
              disabled={!term.active}
              className="font-semibold"
              onCommit={(name) => void run(() => update({ payment_term_code: term.payment_term_code, name }).unwrap(), t('settings.proposalContentSaveFailed', 'Could not save that change.'))}
            />
            {/* Exactly one default — the backend clears the previous one, so this is a radio in
                behaviour even though each row carries its own control. */}
            <label className="flex flex-shrink-0 items-center gap-1.5 text-xs font-semibold text-ui-text2">
              <input
                type="radio"
                name="payment-term-default"
                checked={term.default}
                disabled={!term.active || busy}
                onChange={() => void run(() => update({ payment_term_code: term.payment_term_code, default: true }).unwrap(), t('settings.proposalContentSaveFailed', 'Could not save that change.'))}
              />
              {t('settings.proposalContentDefault', 'Default')}
            </label>
          </div>

          <InlineField
            value={term.description_text ?? ''}
            multiline
            disabled={!term.active}
            placeholder={t('settings.proposalContentPaymentTermSentence', 'The sentence the client reads on the proposal')}
            className="mt-1.5"
            onCommit={(description_text) => void run(() => update({ payment_term_code: term.payment_term_code, description_text }).unwrap(), t('settings.proposalContentSaveFailed', 'Could not save that change.'))}
          />

          <div className="mt-2 space-y-1.5">
            {term.payment_schedule.map((milestone, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <InlineField
                  value={milestone.milestone}
                  disabled={!term.active}
                  onCommit={(value) => patchSchedule(term, idx, { milestone: value })}
                />
                <input
                  type="number"
                  defaultValue={milestone.percentage}
                  disabled={!term.active || busy}
                  onBlur={(e) => {
                    const percentage = Number(e.target.value);
                    if (percentage !== milestone.percentage) patchSchedule(term, idx, { percentage });
                  }}
                  className={cn(fieldClass({ size: 'xs' }), 'w-20 shrink-0 text-right')}
                />
                <span className="flex-shrink-0 text-xs text-ui-text3">%</span>
                <IconButton
                  label={t('settings.proposalContentRemoveMilestone', 'Remove milestone')}
                  size="xs"
                  disabled={!term.active || busy}
                  onClick={() => void run(
                    () => update({
                      payment_term_code: term.payment_term_code,
                      payment_schedule: term.payment_schedule.filter((_, i) => i !== idx),
                    }).unwrap(),
                    t('settings.proposalContentSaveFailed', 'Could not save that change.'),
                  )}
                >
                  ✕
                </IconButton>
              </div>
            ))}
            {/* The total is information, not a rule — a tenant may legitimately run a schedule
                that does not sum to 100 (a fixed mobilisation fee plus percentages). */}
            <div className="flex items-center justify-between pt-0.5">
              <Button
                type="button"
                variant="link"
                size="xs"
                disabled={!term.active || busy}
                onClick={() => void run(
                  () => update({
                    payment_term_code: term.payment_term_code,
                    payment_schedule: [...term.payment_schedule, { milestone: '', percentage: 0 }],
                  }).unwrap(),
                  t('settings.proposalContentSaveFailed', 'Could not save that change.'),
                )}
              >
                + {t('settings.proposalContentAddMilestone', 'Add milestone')}
              </Button>
              <span className="text-xs text-ui-text3">
                {t('settings.proposalContentScheduleTotal', 'Total {{total}}%', {
                  total: term.payment_schedule.reduce((sum, m) => sum + (m.percentage || 0), 0),
                })}
              </span>
            </div>
          </div>
        </Row>
      ))}

      <AddRow
        busy={busy}
        label={t('settings.proposalContentAddPaymentTerm', 'Add payment term')}
        onAdd={() => void run(
          () => create({
            name: t('settings.proposalContentNewPaymentTerm', 'New payment term'),
            payment_schedule: [{ milestone: t('settings.proposalContentOnCompletion', 'Upon job completion'), percentage: 100 }],
          }).unwrap(),
          t('settings.proposalContentAddFailed', 'Could not add that entry.'),
        )}
      />
    </div>
  );
};

// ── Terms & conditions ────────────────────────────────────────────────────────

const TermClausesPanel: React.FC<{ onError: (msg: string) => void }> = ({ onError }) => {
  const { t } = useTranslation();
  const { data: clauses = [], isLoading } = useListTermClausesQuery({ includeInactive: true });
  const [create, { isLoading: creating }] = useCreateTermClauseMutation();
  const [update, { isLoading: updating }] = useUpdateTermClauseMutation();
  const [remove, { isLoading: removing }] = useDeleteTermClauseMutation();
  const busy = creating || updating || removing;

  const run = async (fn: () => Promise<unknown>, fallback: string) => {
    try { await fn(); } catch (e) { onError(apiErrorMessage(e, t, fallback)); }
  };

  if (isLoading) return <LoadingState label={t('common.loading', 'Loading…')} className="py-6" />;

  return (
    <div className="space-y-3">
      <p className="text-xs leading-relaxed text-ui-text3">
        {t(
          'settings.proposalContentTermsHint',
          'Every active clause is placed on a proposal when it is prepared. Wording here is what the client reads, so review it against your own contract.',
        )}
      </p>

      {clauses.map((clause: TermClause) => (
        <Row
          key={clause.clause_code}
          active={clause.active}
          busy={busy}
          onRemove={() => void run(() => remove(clause.clause_code).unwrap(), t('settings.proposalContentRemoveFailed', 'Could not remove that entry.'))}
          onRestore={() => void run(() => update({ clause_code: clause.clause_code, active: true }).unwrap(), t('settings.proposalContentSaveFailed', 'Could not save that change.'))}
        >
          <InlineField
            value={clause.category}
            disabled={!clause.active}
            placeholder={t('settings.proposalContentClauseCategory', 'Category, e.g. WEATHER_DELAY')}
            className="font-semibold uppercase tracking-wide"
            onCommit={(category) => void run(() => update({ clause_code: clause.clause_code, category }).unwrap(), t('settings.proposalContentSaveFailed', 'Could not save that change.'))}
          />
          <InlineField
            value={clause.text}
            multiline
            disabled={!clause.active}
            className="mt-1.5"
            onCommit={(text) => void run(() => update({ clause_code: clause.clause_code, text }).unwrap(), t('settings.proposalContentSaveFailed', 'Could not save that change.'))}
          />
        </Row>
      ))}

      <AddRow
        busy={busy}
        label={t('settings.proposalContentAddClause', 'Add clause')}
        onAdd={() => void run(
          () => create({ category: 'GENERAL', text: t('settings.proposalContentNewClause', 'New clause — replace this with your own wording.') }).unwrap(),
          t('settings.proposalContentAddFailed', 'Could not add that entry.'),
        )}
      />
    </div>
  );
};

// ── Exclusions ────────────────────────────────────────────────────────────────

const ExclusionsPanel: React.FC<{ onError: (msg: string) => void }> = ({ onError }) => {
  const { t } = useTranslation();
  const { data: exclusions = [], isLoading } = useListExclusionsQuery({ includeInactive: true });
  const [create, { isLoading: creating }] = useCreateExclusionMutation();
  const [update, { isLoading: updating }] = useUpdateExclusionMutation();
  const [remove, { isLoading: removing }] = useDeleteExclusionMutation();
  const busy = creating || updating || removing;

  const run = async (fn: () => Promise<unknown>, fallback: string) => {
    try { await fn(); } catch (e) { onError(apiErrorMessage(e, t, fallback)); }
  };

  if (isLoading) return <LoadingState label={t('common.loading', 'Loading…')} className="py-6" />;

  return (
    <div className="space-y-3">
      <p className="text-xs leading-relaxed text-ui-text3">
        {t(
          'settings.proposalContentExclusionsHint',
          'What your pricing does not cover. These appear on the proposal so a client cannot assume work that was never quoted.',
        )}
      </p>

      {exclusions.map((item: ExclusionItem) => (
        <Row
          key={item.exclusion_code}
          active={item.active}
          busy={busy}
          onRemove={() => void run(() => remove(item.exclusion_code).unwrap(), t('settings.proposalContentRemoveFailed', 'Could not remove that entry.'))}
          onRestore={() => void run(() => update({ exclusion_code: item.exclusion_code, active: true }).unwrap(), t('settings.proposalContentSaveFailed', 'Could not save that change.'))}
        >
          <InlineField
            value={item.text}
            multiline
            disabled={!item.active}
            onCommit={(text) => void run(() => update({ exclusion_code: item.exclusion_code, text }).unwrap(), t('settings.proposalContentSaveFailed', 'Could not save that change.'))}
          />
        </Row>
      ))}

      <AddRow
        busy={busy}
        label={t('settings.proposalContentAddExclusion', 'Add exclusion')}
        onAdd={() => void run(
          () => create({ text: t('settings.proposalContentNewExclusion', 'New exclusion — describe what is not included.') }).unwrap(),
          t('settings.proposalContentAddFailed', 'Could not add that entry.'),
        )}
      />
    </div>
  );
};

// ── Warranty terms ────────────────────────────────────────────────────────────

const WarrantyTermsPanel: React.FC<{ onError: (msg: string) => void }> = ({ onError }) => {
  const { t } = useTranslation();
  const { data: warranties = [], isLoading } = useListWarrantyTermsQuery({ includeInactive: true });
  const [create, { isLoading: creating }] = useCreateWarrantyTermMutation();
  const [update, { isLoading: updating }] = useUpdateWarrantyTermMutation();
  const [remove, { isLoading: removing }] = useDeleteWarrantyTermMutation();
  const busy = creating || updating || removing;

  const run = async (fn: () => Promise<unknown>, fallback: string) => {
    try { await fn(); } catch (e) { onError(apiErrorMessage(e, t, fallback)); }
  };

  if (isLoading) return <LoadingState label={t('common.loading', 'Loading…')} className="py-6" />;

  return (
    <div className="space-y-3">
      <p className="text-xs leading-relaxed text-ui-text3">
        {t(
          'settings.proposalContentWarrantyHint',
          'The warranty you offer on a proposal. Terms marked Default are placed on every new proposal — more than one can be, since workmanship and manufacturer cover normally run together.',
        )}
      </p>

      {warranties.map((w: WarrantyTerm) => (
        <Row
          key={w.warranty_term_code}
          active={w.active}
          busy={busy}
          onRemove={() => void run(() => remove(w.warranty_term_code).unwrap(), t('settings.proposalContentRemoveFailed', 'Could not remove that entry.'))}
          onRestore={() => void run(() => update({ warranty_term_code: w.warranty_term_code, active: true }).unwrap(), t('settings.proposalContentSaveFailed', 'Could not save that change.'))}
        >
          <div className="flex items-center gap-2">
            <InlineField
              value={w.title}
              disabled={!w.active}
              className="font-semibold"
              onCommit={(title) => void run(() => update({ warranty_term_code: w.warranty_term_code, title }).unwrap(), t('settings.proposalContentSaveFailed', 'Could not save that change.'))}
            />
            <div className="w-32 flex-shrink-0">
              <InlineField
                value={w.duration}
                disabled={!w.active}
                placeholder={t('settings.proposalContentWarrantyDuration', 'e.g. 5 years')}
                onCommit={(duration) => void run(() => update({ warranty_term_code: w.warranty_term_code, duration }).unwrap(), t('settings.proposalContentSaveFailed', 'Could not save that change.'))}
              />
            </div>
            <label className="flex flex-shrink-0 items-center gap-1.5 text-xs font-semibold text-ui-text2">
              <Switch
                checked={w.default}
                disabled={!w.active || busy}
                onCheckedChange={(next) => void run(() => update({ warranty_term_code: w.warranty_term_code, default: next }).unwrap(), t('settings.proposalContentSaveFailed', 'Could not save that change.'))}
                aria-label={t('settings.proposalContentDefault', 'Default')}
              />
              {t('settings.proposalContentDefault', 'Default')}
            </label>
          </div>
          <InlineField
            value={w.description ?? ''}
            multiline
            disabled={!w.active}
            placeholder={t('settings.proposalContentWarrantyDesc', 'What the warranty covers, in the words the client reads')}
            className="mt-1.5"
            onCommit={(description) => void run(() => update({ warranty_term_code: w.warranty_term_code, description }).unwrap(), t('settings.proposalContentSaveFailed', 'Could not save that change.'))}
          />
        </Row>
      ))}

      <AddRow
        busy={busy}
        label={t('settings.proposalContentAddWarranty', 'Add warranty term')}
        onAdd={() => void run(
          () => create({
            title: t('settings.proposalContentNewWarranty', 'New warranty term'),
            duration: t('settings.proposalContentNewWarrantyDuration', '1 year'),
          }).unwrap(),
          t('settings.proposalContentAddFailed', 'Could not add that entry.'),
        )}
      />
    </div>
  );
};

// ── Section shell ─────────────────────────────────────────────────────────────

const ProposalContentSection: React.FC = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<CatalogueTab>('payment_terms');
  const [error, setError] = useState('');

  const tabs: { key: CatalogueTab; label: string }[] = [
    { key: 'payment_terms', label: t('settings.proposalContentTabPaymentTerms', 'Payment Terms') },
    { key: 'terms', label: t('settings.proposalContentTabTerms', 'Terms & Conditions') },
    { key: 'exclusions', label: t('settings.proposalContentTabExclusions', 'Exclusions') },
    { key: 'warranty', label: t('settings.proposalContentTabWarranty', 'Warranty') },
  ];

  return (
    <div className="space-y-5">
      <div>
        <p className="mb-0.5 text-sm font-semibold text-ui-text">
          {t('settings.proposalContentTitle', 'Proposal Content')}
        </p>
        <p className="text-xs leading-relaxed text-ui-text3">
          {t(
            'settings.proposalContentSubtitle',
            'The standing content a proposal is built from. Changes apply to proposals prepared from now on — a proposal already issued keeps the wording it was created with.',
          )}
        </p>
      </div>

      <Tabs
        tabs={tabs.map((item) => ({ id: item.key, label: item.label }))}
        value={tab}
        onChange={(id) => { setTab(id as CatalogueTab); setError(''); }}
        label={t('settings.proposalContentTitle', 'Proposal Content')}
      />

      {error && <Alert tone="danger" className="font-medium">{error}</Alert>}

      {tab === 'payment_terms' && <PaymentTermsPanel onError={setError} />}
      {tab === 'terms' && <TermClausesPanel onError={setError} />}
      {tab === 'exclusions' && <ExclusionsPanel onError={setError} />}
      {tab === 'warranty' && <WarrantyTermsPanel onError={setError} />}
    </div>
  );
};

export default ProposalContentSection;
